// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  memo,
  useEffect,
  useState,
  MouseEvent,
  useContext,
  ReactNode,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import {
  mediaMax,
  fontSize,
  fontWeight,
  spacing,
  isMediaMax,
  zIndex,
} from 'presentations/views/styles';
import {
  black,
  gray01,
  gray04,
  primary,
  background,
  white,
} from 'presentations/views/styles/color';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Popover from 'presentations/views/components/atoms/Popover';
import AnonymousUserMenuList from 'presentations/views/components/molecules/AnonymousUserMenuList';
import Logo from 'presentations/views/components/atoms/Logo';
import {
  homePath,
  dashboardPath,
  signUpPath,
  pricingPath,
  loginPath,
  featureListPath,
  downloadsPath,
  productIntegrationsPath,
  newsListPath,
  contactPath,
  resourceListPath,
  productSecurityPath,
  customerStoryListPath,
  changelogListPath,
} from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';
import { authRedirectUrl } from 'presentations/routes/helper';
import Button from 'presentations/views/components/atoms/Button';
import { isInElectron } from 'presentations/views/helpers/electron';
import { ServicePortalContext } from 'presentations/reducers/ServicePortal';
import { developerPath } from 'presentations/routes/path/developer';

interface Props {
  className?: string;
  viewer: ViewerItem | null;
  isLoading?: boolean;
  isHideServiceMenu?: boolean;
  isHideLogin?: boolean;
  backgroundNode?: ReactNode;
  isTransparent?: boolean;
  isDarkable?: boolean;
}

const LoadingComponent = ({ isMobile }: { isMobile: boolean }) => {
  return isMobile ? (
    <>
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
    </>
  ) : (
    <>
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
      &nbsp;
      <Skeleton width={32} />
    </>
  );
};

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const [to, setTo] = useState<string>(authRedirectUrl(dashboardPath()));
  const [productAnchor, setProductAnchor] = useState<null | HTMLElement>(null);
  const [resourceAnchor, setResourceAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [isElectron, setIsElectron] = useState<boolean>(false);

  const { state: serviceState } = useContext(ServicePortalContext);
  const isLoading = props.isLoading || !serviceState.isInitialized;

  const router = useRouter();
  useEffect(() => {
    if (window.location.pathname !== homePath()) {
      setTo(authRedirectUrl(window.location.pathname + window.location.search));
    }
    setIsElectron(isInElectron());
  }, []);

  const onClickProductMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setProductAnchor(productAnchor !== null ? null : e.currentTarget);
  };

  const onCloseProductMenu = () => {
    setProductAnchor(null);
  };

  const onClickResourceMenu = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setResourceAnchor(resourceAnchor !== null ? null : e.currentTarget);
  };

  const onCloseResourceMenu = () => {
    setResourceAnchor(null);
  };

  const isMobile = isMediaMax('tablet');
  const productList = [
    {
      label: t('Feature'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="-" />,
      link: featureListPath(),
    },
    {
      label: t('Downloads'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="-" />,
      link: downloadsPath(),
    },
    {
      label: t('Integrations'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="-" />,
      link: productIntegrationsPath(),
    },
    {
      label: t('Security'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="-" />,
      link: productSecurityPath(),
    },
  ];

  const resourceList = [
    {
      label: t('Customer Stories'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
      link: customerStoryListPath(),
    },
    {
      label: t('Changelog'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
      link: changelogListPath(),
    },
    {
      label: t('News'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
      link: newsListPath(),
    },
    {
      label: t('Magazine'),
      undefined,
      icon: <ListIcon src="pages/top/icon-bar.svg" alt="" />,
      link: resourceListPath(),
    },
  ];

  const [isHomeDrawerOpen, setIsHomeDrawerOpen] = useState<boolean>(false);

  const clickHomeDrawer = () => {
    setIsHomeDrawerOpen(!isHomeDrawerOpen);
  };

  return (
    <Container
      className={props.className}
      isElectron={isElectron}
      isTransparent={props.isTransparent}
    >
      {props.backgroundNode}
      <LeftBox isElectron={isElectron}>
        {!isMobile && (
          <Link href={homePath()}>
            <ServiceImage
              width={130}
              height={34}
              variant={props.isDarkable ? 'white' : undefined}
            />
          </Link>
        )}
        {isMobile && (
          <DashboardBackMenuListItem>
            <AnonymousUserMenuList
              currentUrl={to}
              viewer={props.viewer}
              isDrawerOpen={isHomeDrawerOpen}
              onClick={clickHomeDrawer}
              isDarkable={props.isDarkable}
            />
          </DashboardBackMenuListItem>
        )}
        {!isMobile && !props.isHideServiceMenu && (
          <Navigation>
            <NavigationItem>
              <NavigationLink
                onClick={onClickProductMenu}
                isDarkable={props.isDarkable}
              >
                {t('Product')}
                <ExpandIcon
                  name={productAnchor ? 'expand_less' : 'expand_more'}
                  size={fontSize(20)}
                />
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink
                href={developerPath()}
                isDarkable={props.isDarkable}
              >
                {t('Developers')}
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink
                href={pricingPath()}
                isDarkable={props.isDarkable}
              >
                {t('Pricing')}
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink
                onClick={onClickResourceMenu}
                isDarkable={props.isDarkable}
              >
                {t('Resource')}
                <ExpandIcon
                  name={resourceAnchor ? 'expand_less' : 'expand_more'}
                  size={fontSize(20)}
                />
              </NavigationLink>
            </NavigationItem>
            {productAnchor && (
              <Popover
                onClose={onCloseProductMenu}
                anchor={productAnchor}
                anchorReference="anchorEl"
              >
                <MenuItemList>
                  {productList.map((menu) => (
                    <MenuItem key={menu.label} href={menu.link}>
                      {menu.icon}
                      <MenuItemName>{menu.label}</MenuItemName>
                    </MenuItem>
                  ))}
                </MenuItemList>
              </Popover>
            )}
            {resourceAnchor && (
              <Popover
                onClose={onCloseResourceMenu}
                anchor={resourceAnchor}
                anchorReference="anchorEl"
              >
                <MenuItemList>
                  {resourceList.map((menu) => (
                    <MenuItem key={menu.label} href={menu.link}>
                      {menu.icon}
                      <MenuItemName>{menu.label}</MenuItemName>
                    </MenuItem>
                  ))}
                </MenuItemList>
              </Popover>
            )}
          </Navigation>
        )}
      </LeftBox>
      <BrandContainer>
        {isMobile && (
          <Link href={homePath()}>
            <ServiceImage
              width={130}
              height={34}
              variant={props.isDarkable ? 'white' : undefined}
            />
          </Link>
        )}
      </BrandContainer>
      <RightBox>
        <MenuList>
          {!isMobile && (
            <MenuListItem>
              <NavigationNativeLink
                href={contactPath()}
                isDarkable={props.isDarkable}
              >
                {t('Contact')}
              </NavigationNativeLink>
            </MenuListItem>
          )}
          {isLoading ? (
            <LoadingComponent isMobile={isMobile} />
          ) : props.viewer ? (
            <MenuListItem>
              <DashboardButton
                href={dashboardPath()}
                color="primary"
                isDarkable={props.isDarkable}
              >
                {t('Home')}
              </DashboardButton>
            </MenuListItem>
          ) : (
            !props.isHideLogin && (
              <>
                {!isMobile && (
                  <MenuListItem>
                    <LoginButton
                      isDarkable={props.isDarkable}
                      href={loginPath({
                        to,
                        locale: router.locale,
                      })}
                      color="inherit"
                      variant="text"
                    >
                      {t('Sign In')}
                    </LoginButton>
                  </MenuListItem>
                )}

                <MenuListItem>
                  <SignUpButton
                    isDarkable={props.isDarkable}
                    href={signUpPath({
                      to,
                      locale: router.locale,
                    })}
                    color="primary"
                  >
                    {t('Sign Up')}
                  </SignUpButton>
                </MenuListItem>
              </>
            )
          )}
        </MenuList>
      </RightBox>
    </Container>
  );
});

const Container = styled.div<{ isElectron: boolean; isTransparent?: boolean }>`
  display: flex;
  align-items: center;
  height: 80px;
  position: sticky;
  top: 0;
  background-color: ${(props) =>
    props.isTransparent ? 'transparent' : background};
  transition: all 300ms ease-out;
  border-bottom: ${(props) =>
    !props.isTransparent && `1px solid ${gray04(props)}`};
  -webkit-app-region: drag;
  z-index: ${zIndex(100)};
  ${mediaMax.tablet} {
    height: 54px;
    /* z-index: ${zIndex(1400)}; */
    justify-content: space-between;
  }
  ${({ isElectron }) =>
    isElectron
      ? css`
          height: 42px;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: ${zIndex(1400)};
        `
      : ''}
`;
const LeftBox = styled.div<{
  isElectron: boolean;
}>`
  margin-right: auto;
  margin-left: ${({ isElectron }) => (isElectron ? spacing(40) : spacing(24))};
  display: flex;
  align-items: center;
  z-index: 1;
  ${mediaMax.tablet} {
    margin-left: ${spacing(16)};
    min-width: 80px;
    margin-right: initial;
  }
  ${mediaMax.xs} {
    width: 34px;
    overflow: hidden;
  }
`;
const Navigation = styled.ul`
  margin-left: ${spacing(48)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const NavigationItem = styled.li`
  margin-right: ${spacing(24)};
  :last-of-type {
    margin-right: 0;
  }
`;
const NavigationLink = styled(Link)<{ isDarkable?: boolean }>`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  letter-spacing: 0.01em;
  color: ${(props) => (props.isDarkable ? white : black)};
  &:hover {
    color: ${(props) => (props.isDarkable ? white : black)};
  }
  display: flex;
  align-items: center;
`;
const NavigationNativeLink = styled.a<{ isDarkable?: boolean }>`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  letter-spacing: 0.01em;
  color: ${(props) => (props.isDarkable ? white : black)};
  &:hover {
    color: ${(props) => (props.isDarkable ? white : black)};
  }
  display: flex;
  align-items: center;
`;
const ServiceImage = styled(Logo)`
  width: 130px;
  height: auto;

  ${mediaMax.tablet} {
    width: 89px;
  }
`;
const RightBox = styled.div`
  margin-right: ${spacing(24)};
  z-index: 1;
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
    min-width: 80px;
  }
  ${mediaMax.xs} {
    margin-right: ${spacing(3)};
  }
`;
const MenuList = styled.ul`
  display: flex;
  align-items: center;
`;
const MenuListItem = styled.li`
  margin-right: ${spacing(20)};
  color: ${gray01};
  :last-of-type {
    margin-right: 0;
  }
`;
const DashboardBackMenuListItem = styled.div<{ isElectron?: boolean }>`
  margin-right: ${spacing(24)};
  padding-right: ${spacing(24)};
  border-right: 1px solid ${gray04};
  /* color: ${gray01}; */
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-right: 0;
    padding-right: 0;
    border-right: none;

    ${({ isElectron }) =>
      isElectron &&
      css`
        margin-left: ${spacing(64)};
      `}
  }
`;

const ListIcon = styled(Image)`
  font-size: ${fontSize(24)} !important;
  /* color: ${gray01}; */
  padding-left: 6px;
  width: 16px;
`;
const BrandContainer = styled.div`
  flex: 1;
  text-align: center;
`;
const ExpandIcon = styled(Icon)`
  min-width: 20px;
  min-height: 20px;
`;
const MenuItemList = styled.div`
  padding: ${spacing(16)};
`;
const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
  /* color: ${gray01}; */
  :hover {
    color: ${primary};
  }
`;
const MenuItemName = styled.div`
  margin-left: ${spacing(12)};
  /* color: ${gray01}; */
`;
const DashboardButton = styled(Button)<{ isDarkable?: boolean }>`
  background-color: ${(props) => props.isDarkable && white};
  color: ${(props) => props.isDarkable && black};
  :hover {
    background-color: ${(props) => props.isDarkable && white};
    color: ${(props) => props.isDarkable && black};
  }
  ${mediaMax.tablet} {
    width: 96px;
    padding: ${spacing(6)} ${spacing(6)};
    letter-spacing: 0;
    font-size: ${fontSize(12)};
  }
`;
const SignUpButton = styled(Button)<{ isDarkable?: boolean }>`
  background-color: ${(props) => props.isDarkable && white};
  color: ${(props) => props.isDarkable && black};
  :hover {
    background-color: ${(props) => props.isDarkable && white};
    color: ${(props) => props.isDarkable && black};
  }
`;
const LoginButton = styled(Button)<{ isDarkable?: boolean }>`
  color: ${(props) => props.isDarkable && white};
`;

export default Component;
